import { ParamMap } from '@angular/router';
import {
  EXPENSE_MANAGEMENT_MENU,
  CompanyPageSubMenu,
  CompanyPermission,
} from '../models/company-permission.model';
import { RoleConstant } from '../models/role-constant';
import { ServiceRequestStatus } from '../models/service-request-status';
import {
  getCompanyPermissionForCompany,
  getPageUrlQueryParams,
  isExpenseSubMenuRoute,
  isExpenseSubMenuRouteActive,
  normalizeRoutePath,
  parsePageUrlParts,
} from './company-permission.util';

/**
 * Query keys used by BE for Expense list tabs (`expenses/list?{key}=`).
 * Matches /my pagePermissions (e.g. pendingapproval=, allexpense=).
 * Plain `expenses/list` (no query) is not a tab — ignore “Expense Management” entry.
 */
export const EXPENSE_LIST_TAB_QUERY_KEYS = [
  'overview',
  'draft',
  'pendingapproval',
  'pendingpayment',
  'allexpense',
  // Legacy aliases (older FE guesses / local fixtures)
  'approval',
  'payment',
  'all',
  'allexpenses',
  'expenses',
] as const;

export type ExpenseListTabQueryKey =
  (typeof EXPENSE_LIST_TAB_QUERY_KEYS)[number];

/** Keys that represent the “All Expenses” tab. */
export const EXPENSE_ALL_TAB_QUERY_KEYS: readonly ExpenseListTabQueryKey[] = [
  'allexpense',
  'all',
  'allexpenses',
  'expenses',
];

/** Horizontal tab order (Figma Expense Management revamp). */
export const FIGMA_EXPENSE_LIST_TAB_ORDER: readonly ExpenseListTabQueryKey[] = [
  'overview',
  'draft',
  'pendingapproval',
  'approval',
  'pendingpayment',
  'payment',
  'allexpense',
  'all',
  'allexpenses',
  'expenses',
];

export const EXPENSE_LIST_TAB_DISPLAY_LABELS: Partial<
  Record<ExpenseListTabQueryKey, string>
> = {
  overview: 'Overview',
  draft: 'Drafts',
  pendingapproval: 'Pending approval',
  approval: 'Pending approval',
  pendingpayment: 'Pending payment',
  payment: 'Pending payment',
  allexpense: 'All Expenses',
  all: 'All Expenses',
  allexpenses: 'All Expenses',
  expenses: 'All Expenses',
};

export interface ExpenseListTabViewModel {
  page: string;
  pageUrl: string;
  queryParams: Record<string, string>;
  queryKey: ExpenseListTabQueryKey | null;
}

export const EXPENSE_OVERVIEW_STATUS_FILTER_KEY = 'OverviewStatus';
export const EXPENSE_STATUS_FILTER_KEY = 'status';

export const EXPENSE_OVERVIEW_TAB_PAGE_SIZE = 16;
/** Auditor 2 / C-Admin Overview — Needs approval queue only. */
export const EXPENSE_WORK_ITEMS_OVERVIEW_TAB_PAGE_SIZE = 8;
/** Auditor 1 Overview loads Needs approval + drafts + returned in one page. */
export const EXPENSE_AUDITOR1_OVERVIEW_TAB_PAGE_SIZE = 32;
export const EXPENSE_LIST_DEFAULT_PAGE_SIZE = 10;

export type ExpenseListTabPreset = {
  mode: 'overview' | 'table';
  filters?: Record<string, unknown>;
  pageSize?: number;
};

export function expenseOverviewStatusFilter(
  statuses: ServiceRequestStatus[]
): Record<string, string[]> {
  return { [EXPENSE_OVERVIEW_STATUS_FILTER_KEY]: statuses };
}

export function expenseStatusFilter(
  status: ServiceRequestStatus
): Record<string, string> {
  return { [EXPENSE_STATUS_FILTER_KEY]: status };
}

/** Auditor 2 / C-Admin — work-item Overview (Needs approval only). */
export function isExpenseOverviewWorkItemsRole(
  roleId: string | null | undefined
): boolean {
  if (!roleId) {
    return false;
  }
  return (
    roleId === RoleConstant.auditor2 ||
    roleId === RoleConstant.companyAdminId
  );
}

/**
 * Auditor 1 — hybrid Overview: Needs approval + Ready to Review (drafts) +
 * Returned Expenses (creator + approver).
 */
export function isExpenseOverviewHybridRole(
  roleId: string | null | undefined
): boolean {
  return roleId === RoleConstant.auditor1;
}

/**
 * Roles that approve on Pending approval (bulk + row actions).
 * Auditor 1 is also a creator — hybrid Overview, but approval actions still
 * apply on the Pending approval tab.
 */
export function isExpenseApprovalActorRole(
  roleId: string | null | undefined
): boolean {
  if (!roleId) {
    return false;
  }
  return (
    roleId === RoleConstant.auditor1 ||
    roleId === RoleConstant.auditor2 ||
    roleId === RoleConstant.companyAdminId
  );
}

/**
 * Overview empty uses the upload dropzone (creators + Auditor 1 hybrid).
 * Auditor 2 / C-Admin keep the work-items empty state instead.
 */
export function isExpenseOverviewUploadEmptyRole(
  roleId: string | null | undefined
): boolean {
  if (!roleId || isExpenseOverviewWorkItemsRole(roleId)) {
    return false;
  }
  return true;
}

/** Company Admin expense Overview: approval queue only (Figma / AP C-Admin parity). */
export function isExpenseOverviewApprovalOnlyRole(
  roleId: string | null | undefined
): boolean {
  return roleId === RoleConstant.companyAdminId;
}

export function resolveExpenseOverviewTabPageSize(
  roleId: string | null | undefined
): number {
  if (isExpenseOverviewHybridRole(roleId)) {
    return EXPENSE_AUDITOR1_OVERVIEW_TAB_PAGE_SIZE;
  }
  if (isExpenseOverviewWorkItemsRole(roleId)) {
    return EXPENSE_WORK_ITEMS_OVERVIEW_TAB_PAGE_SIZE;
  }
  return EXPENSE_OVERVIEW_TAB_PAGE_SIZE;
}

export function resolveExpenseOverviewTabFilters(
  roleId: string | null | undefined
): Record<string, string[]> {
  if (isExpenseOverviewApprovalOnlyRole(roleId)) {
    return expenseOverviewStatusFilter([
      ServiceRequestStatus.pendingApproval,
    ]);
  }

  if (isExpenseOverviewHybridRole(roleId)) {
    return expenseOverviewStatusFilter([
      ServiceRequestStatus.pendingApproval,
      ServiceRequestStatus.draft,
      ServiceRequestStatus.returned,
    ]);
  }

  if (isExpenseOverviewWorkItemsRole(roleId)) {
    return expenseOverviewStatusFilter([
      ServiceRequestStatus.pendingApproval,
    ]);
  }

  // Creator / operational hierarchy Overview.
  return expenseOverviewStatusFilter([
    ServiceRequestStatus.draft,
    ServiceRequestStatus.returned,
  ]);
}

export function isExpenseListTabPageUrl(pageUrl: string): boolean {
  if (!isExpenseSubMenuRoute(pageUrl)) {
    return false;
  }

  const { path, queryParams } = parsePageUrlParts(pageUrl);
  if (path.toLowerCase() !== 'expenses/list') {
    return false;
  }

  if (!queryParams) {
    return false;
  }

  return EXPENSE_LIST_TAB_QUERY_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(queryParams, key)
  );
}

export function getExpenseListTabSubMenus(
  companyPermission: CompanyPermission | null | undefined
): CompanyPageSubMenu[] {
  const pagePermission = companyPermission?.pagePermissions?.find(
    (entry) => entry.menu === EXPENSE_MANAGEMENT_MENU
  );

  return (pagePermission?.subMenus ?? []).filter((subMenu) =>
    isExpenseListTabPageUrl(subMenu.pageUrl)
  );
}

export function getExpenseListTabs(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): ExpenseListTabViewModel[] {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  return getExpenseListTabSubMenus(companyPermission).map((subMenu) => {
    const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
    const queryKey =
      EXPENSE_LIST_TAB_QUERY_KEYS.find((key) =>
        Object.prototype.hasOwnProperty.call(queryParams, key)
      ) ?? null;

    return {
      page: subMenu.page,
      pageUrl: subMenu.pageUrl,
      queryParams,
      queryKey,
    };
  });
}

export function normalizeExpenseListTabKey(
  key: ExpenseListTabQueryKey | null
): ExpenseListTabQueryKey | null {
  if (!key) {
    return null;
  }
  if ((EXPENSE_ALL_TAB_QUERY_KEYS as readonly string[]).includes(key)) {
    return key;
  }
  return key;
}

export function isExpenseAllExpensesTabKey(
  key: ExpenseListTabQueryKey | null
): boolean {
  return (
    !!key &&
    (EXPENSE_ALL_TAB_QUERY_KEYS as readonly string[]).includes(key)
  );
}

export function sortExpenseListTabsForDesign(
  tabs: ExpenseListTabViewModel[]
): ExpenseListTabViewModel[] {
  return [...tabs]
    .sort((a, b) => {
      const ai = a.queryKey
        ? FIGMA_EXPENSE_LIST_TAB_ORDER.indexOf(a.queryKey)
        : -1;
      const bi = b.queryKey
        ? FIGMA_EXPENSE_LIST_TAB_ORDER.indexOf(b.queryKey)
        : -1;
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
    .map((tab) => {
      const label =
        tab.queryKey && EXPENSE_LIST_TAB_DISPLAY_LABELS[tab.queryKey]
          ? EXPENSE_LIST_TAB_DISPLAY_LABELS[tab.queryKey]!
          : tab.page;
      return { ...tab, page: label };
    });
}

export function sortExpenseNavSubMenusForDesign(
  subMenus: CompanyPageSubMenu[]
): CompanyPageSubMenu[] {
  const decorated = (subMenus ?? []).map((subMenu, index) => {
    const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
    const queryKey =
      EXPENSE_LIST_TAB_QUERY_KEYS.find((key) =>
        Object.prototype.hasOwnProperty.call(queryParams, key)
      ) ?? null;
    const order = queryKey
      ? FIGMA_EXPENSE_LIST_TAB_ORDER.indexOf(queryKey)
      : -1;
    const page =
      queryKey && EXPENSE_LIST_TAB_DISPLAY_LABELS[queryKey]
        ? EXPENSE_LIST_TAB_DISPLAY_LABELS[queryKey]!
        : subMenu.page;
    return { subMenu: { ...subMenu, page }, order, index };
  });

  return decorated
    .sort((a, b) => {
      const ai = a.order === -1 ? 1000 + a.index : a.order;
      const bi = b.order === -1 ? 1000 + b.index : b.order;
      return ai - bi;
    })
    .map((entry) => entry.subMenu);
}

export function resolveExpenseListTabQueryKey(
  query: ParamMap | URLSearchParams | null | undefined
): ExpenseListTabQueryKey | null {
  if (!query) {
    return null;
  }

  const hasKey = (key: string) =>
    query instanceof URLSearchParams ? query.has(key) : query.has(key);

  return EXPENSE_LIST_TAB_QUERY_KEYS.find((key) => hasKey(key)) ?? null;
}

export function resolveExpenseListTabPreset(
  query: ParamMap | URLSearchParams | null | undefined,
  selectedRoleId?: string | null
): ExpenseListTabPreset {
  const tabKey = resolveExpenseListTabQueryKey(query);

  switch (tabKey) {
    case 'overview':
      return {
        mode: 'overview',
        filters: resolveExpenseOverviewTabFilters(selectedRoleId),
        pageSize: resolveExpenseOverviewTabPageSize(selectedRoleId),
      };
    case 'draft':
      return {
        mode: 'table',
        filters: expenseStatusFilter(ServiceRequestStatus.draft),
      };
    case 'pendingapproval':
    case 'approval':
      return {
        mode: 'table',
        filters: expenseStatusFilter(ServiceRequestStatus.pendingApproval),
      };
    case 'pendingpayment':
    case 'payment':
      return {
        mode: 'table',
        filters: expenseStatusFilter(ServiceRequestStatus.paymentPending),
      };
    case 'allexpense':
    case 'all':
    case 'allexpenses':
    case 'expenses':
      return { mode: 'table' };
    default:
      return { mode: 'table' };
  }
}

export function isExpenseListTabActive(
  tab: ExpenseListTabViewModel,
  pathname: string,
  query: URLSearchParams | ParamMap | null | undefined
): boolean {
  const path = normalizeRoutePath(pathname);
  if (path !== 'expenses/list' && path !== 'expenses') {
    return false;
  }

  if (!tab.queryKey || !query) {
    return false;
  }

  return query instanceof URLSearchParams
    ? query.has(tab.queryKey)
    : query.has(tab.queryKey);
}

export function isExpenseListTabUrlAllowed(
  url: string,
  subMenus: CompanyPageSubMenu[] | null | undefined
): boolean {
  const path = normalizeRoutePath(url.split('?')[0]);
  if (path !== 'expenses/list' && path !== 'expenses') {
    return true;
  }

  const queryString = url.includes('?') ? url.split('?')[1].split('#')[0] : '';
  const query = new URLSearchParams(queryString);
  const tabKey = resolveExpenseListTabQueryKey(query);

  if (!tabKey) {
    return true;
  }

  const menus = subMenus ?? [];
  return menus.some((subMenu) =>
    isExpenseSubMenuRouteActive(subMenu.pageUrl, path, query)
  );
}

/** Default list tab query when opening `/expenses/list` from dashboard drilldowns. */
export function expenseListDashboardDrilldownQueryParams(): Record<string, string> {
  return { allexpense: '' };
}

export function isExpensePendingApprovalTabKey(
  key: ExpenseListTabQueryKey | null
): boolean {
  return key === 'pendingapproval' || key === 'approval';
}

export function isExpensePendingPaymentTabKey(
  key: ExpenseListTabQueryKey | null
): boolean {
  return key === 'pendingpayment' || key === 'payment';
}

/**
 * BE saved-filter `tab` key — distinct from URL query keys
 * (e.g. URL `allexpense=` maps to BE `allexpenses`).
 */
export function resolveExpenseSavedFilterTabKey(
  tabKey?: ExpenseListTabQueryKey | null
): string {
  if (!tabKey) {
    return 'list';
  }

  switch (tabKey) {
    case 'allexpense':
    case 'all':
    case 'expenses':
      return 'allexpenses';
    case 'approval':
      return 'pendingapproval';
    case 'payment':
      return 'pendingpayment';
    default:
      return tabKey;
  }
}
